import { useState, FC, FocusEvent, useEffect } from "react";
import { Check, Clear } from "@mui/icons-material";
import { Box, IconButton, SxProps, TextField, Theme } from "@mui/material";
import { makeSxStyles } from "ui";

interface CustomTextFieldComponentProps {
  initialValue: string;
  saveChanges: (text: string) => void;
  buttonPositioning?: "inline" | "down";
}

const InlineEditField: FC<CustomTextFieldComponentProps> = ({
  initialValue,
  saveChanges,
  buttonPositioning = "inline",
}) => {
  const styles = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(initialValue);

  const handleCancel = () => {
    setValue(initialValue);
    setIsEditing(false);
  };

  const handleConfirm = () => {
    saveChanges(value);
    setIsEditing(false);
  };

  const handleBlur = (
    e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // On blur, revert changes if user hasn't clicked the confirm button
    const clickedConfirm = e.relatedTarget?.closest(".confirm-button");
    if (!clickedConfirm) {
      setValue(initialValue);
      setIsEditing(false);
    }
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <Box
      sx={
        {
          ...styles.container,
          flexDirection: buttonPositioning === "down" ? "column" : "row",
        } as SxProps<Theme>
      }
    >
      <Box sx={{ ...styles.border, opacity: isEditing ? 1 : 0 }} />
      <TextField
        variant="standard"
        fullWidth
        multiline
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setIsEditing(true)}
        onBlur={handleBlur}
        InputProps={{
          disableUnderline: true,
        }}
        inputProps={{
          style: {
            cursor: !isEditing ? "pointer" : "text",
          },
        }}
      />

      {isEditing && (
        <Box
          sx={{
            ...styles.buttonContainer,
            ...(buttonPositioning === "down" && {
              justifyContent: "flex-end",
              width: "100%",
              marginTop: "16px",
            }),
          }}
        >
          <IconButton onClick={handleCancel}>
            <Clear />
          </IconButton>
          <IconButton className="confirm-button" onClick={handleConfirm}>
            <Check />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default InlineEditField;

const useStyles = makeSxStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    position: "relative",
  },
  border: {
    position: "absolute",
    top: 0,
    left: -6,
    right: -20,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 2,
  },
  buttonContainer: {
    display: "flex",
    alignItems: "center",
    paddingRight: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
}));
